/**
 * Firestore sync for Invoices.
 * Uses collections/invoices. Real-time listener keeps local state in sync.
 */
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase_analytics';

const COLLECTION = 'invoices';
const MIGRATION_KEY = 'invoices_migrated_to_firestore';

function invoicesRef() {
  return collection(db, COLLECTION);
}

function invoiceDoc(id) {
  return doc(db, COLLECTION, id);
}

/** Normalize invoice for Firestore (dates/arrays are OK as-is for Firestore) */
function toFirestore(invoice) {
  const { id, ...rest } = invoice;
  return {
    ...rest,
    updatedAt: serverTimestamp(),
  };
}

/** Add id from doc and normalize for app (e.g. createdAt) */
function fromFirestore(id, data) {
  if (!data) return null;
  return {
    id,
    ...data,
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() ?? data.updatedAt,
  };
}

/**
 * One-time migration: push existing localStorage invoices to Firestore.
 * Call once on load if migration not done yet.
 */
export async function migrateInvoicesFromLocalStorage() {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(MIGRATION_KEY)) return;

  const raw = localStorage.getItem('invoices');
  if (!raw) {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  let list;
  try {
    list = JSON.parse(raw);
  } catch {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  if (!Array.isArray(list) || list.length === 0) {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  for (const inv of list) {
    const id = inv.id || `inv_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const payload = toFirestore({ ...inv, id });
    if (!payload.createdAt) payload.createdAt = serverTimestamp();
    await setDoc(invoiceDoc(id), payload, { merge: true });
  }

  localStorage.setItem(MIGRATION_KEY, 'true');
}

/**
 * Fetch all invoices once (no real-time).
 */
export async function getInvoices() {
  const q = query(invoicesRef(), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromFirestore(d.id, d.data()));
}

/**
 * Subscribe to invoices collection. Callback receives array of invoices on every change.
 * Returns unsubscribe function.
 */
export function subscribeInvoices(callback) {
  const q = query(invoicesRef(), orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => fromFirestore(d.id, d.data()));
    callback(list);
  });
}

/**
 * Create or update an invoice. Use id for create (e.g. Date.now().toString()) or existing id for update.
 */
export async function saveInvoice(invoice) {
  const id = invoice.id || `inv_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const payload = toFirestore({ ...invoice, id });
  if (!payload.createdAt) payload.createdAt = serverTimestamp();
  await setDoc(invoiceDoc(id), payload, { merge: true });
  return id;
}

/**
 * Delete an invoice by id.
 */
export async function deleteInvoice(id) {
  await deleteDoc(invoiceDoc(id));
}
